import { Close } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useCallback } from "react";
import { useGetQuestionOptionsQuery } from "src/api/services/questionOptionsApi";
import { useGetQuestionsQuery } from "src/api/services/questionsApi";
import { Question, QuestionOption } from "src/api/types";

type OptionState = {
  position: number;
  questionOption: QuestionOption | null;
  nextQuestion: Question | null;
};

interface IntakeQuestionOptionViewProps {
  index: number;
  option: OptionState;
  setOption: React.Dispatch<React.SetStateAction<OptionState[]>>;
  setError: (err: string) => void;
}

const IntakeQuestionOptionView = ({
  index,
  option,
  setOption,
  setError,
}: IntakeQuestionOptionViewProps) => {
  const { position, questionOption, nextQuestion } = option;

  const handlePosition = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setOption((prev) => [
        ...prev.slice(0, index),
        { ...option, position: Number(event.target.value) },
        ...prev.slice(index + 1),
      ]);
      setError("");
    },
    [index, option, setError, setOption],
  );

  const handleNextQuestion = useCallback(
    (_: React.SyntheticEvent<Element, Event>, value: Question | null) => {
      setOption((prev) => [
        ...prev.slice(0, index),
        { ...option, nextQuestion: value },
        ...prev.slice(index + 1),
      ]);
      setError("");
    },
    [index, option, setError, setOption],
  );

  const handleQuestionOption = useCallback(
    (_: React.SyntheticEvent<Element, Event>, value: QuestionOption | null) => {
      setOption((prev) => [
        ...prev.slice(0, index),
        { ...option, questionOption: value },
        ...prev.slice(index + 1),
      ]);
      setError("");
    },
    [index, option, setError, setOption],
  );

  const { questions } = useGetQuestionsQuery(
    { from: 0, to: 1000 },
    {
      selectFromResult: ({ data }) => {
        return { questions: data?.data ?? [] };
      },
    },
  );
  const { options } = useGetQuestionOptionsQuery(
    { from: 0, to: 1000 },
    {
      selectFromResult: ({ data }) => {
        return { options: data?.data ?? [] };
      },
    },
  );

  const removeOption = useCallback(() => {
    setOption((prev) => [...prev.slice(0, index), ...prev.slice(index + 1)]);
  }, [index, setOption]);

  return (
    <Stack gap={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="body2">{`Question option ${index + 1}:`}</Typography>
        {index > 0 ? (
          <Tooltip title="Remove option">
            <IconButton color="error" onClick={removeOption}>
              <Close />
            </IconButton>
          </Tooltip>
        ) : null}
      </Stack>
      <Autocomplete
        options={options}
        getOptionLabel={(p) => p.label}
        value={questionOption}
        onChange={handleQuestionOption}
        renderInput={(params) => (
          <TextField {...params} label="Question option" required />
        )}
      />
      <TextField
        label="Position"
        type="number"
        required
        value={position ?? ""}
        onChange={handlePosition}
      />
      <Autocomplete
        options={questions}
        getOptionLabel={(p) => `${p.id}: ${p.title}`}
        value={nextQuestion}
        onChange={handleNextQuestion}
        renderInput={(params) => (
          <TextField {...params} label="Next question" />
        )}
        renderOption={(props, option) => {
          const { key, ...optionProps } = props;
          return (
            <Box key={key} {...optionProps} component="li">
              <Stack>
                <Typography textAlign="left">{option.title}</Typography>
                {option.subtitle ? (
                  <Typography
                    textAlign="left"
                    variant="caption"
                    color="text.secondary"
                  >
                    {option.subtitle}
                  </Typography>
                ) : null}
              </Stack>
            </Box>
          );
        }}
      />
    </Stack>
  );
};

export default IntakeQuestionOptionView;
